<script setup lang="ts">
import { ref } from 'vue';
import { useDeviceDetection } from '@/composables/useDeviceDetection';
import { useToast } from '@/composables/useToast';
import { FilePicker, type PickedFile } from '@capawesome/capacitor-file-picker';
import { Capacitor } from '@capacitor/core';

// Define props
defineProps<{
  dense?: boolean
}>();

// Define emits
const emit = defineEmits<{
  'file-loaded': [file: File]
}>();

const { isMobileDevice } = useDeviceDetection();
const { createToast } = useToast();
const dragActive = ref(false);
const dragCounter = ref(0);

// Check if file is a valid MusicXML file
const isValidFile = (fileName: string): boolean => {
  return (
    fileName.endsWith('.xml') ||
    fileName.endsWith('.musicxml') ||
    fileName.endsWith('.mxl')
  );
};

// Convert PickedFile to File object
const convertPickedFileToFile = async (pickedFile: PickedFile): Promise<File> => {
  const isWeb = Capacitor.getPlatform() === 'web';
  
  if (isWeb && pickedFile.blob) {
    // On Web, the blob is already available
    return new File([pickedFile.blob], pickedFile.name, {
      type: pickedFile.mimeType || 'application/octet-stream'
    });
  } else if (pickedFile.data) {
    // On mobile (iOS/Android), convert base64 data to blob
    try {
      const binaryString = atob(pickedFile.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], {
        type: pickedFile.mimeType || 'application/octet-stream'
      });
      return new File([blob], pickedFile.name, {
        type: pickedFile.mimeType || 'application/octet-stream'
      });
    } catch (error) {
      console.error('Error converting base64 to file:', error);
      throw error;
    }
  }
  
  throw new Error('Unable to convert picked file to File object');
};

// Trigger file picker
const triggerFileInput = async () => {
  try {
    // Use MusicXML-specific MIME types for filtering
    const result = await FilePicker.pickFiles({
      limit: 1,
      readData: true
    });

    if (result.files && result.files.length > 0) {
      const pickedFile = result.files[0];
      
      if (isValidFile(pickedFile.name)) {
        // Convert the picked file to a standard File object
        const file = await convertPickedFileToFile(pickedFile);
        // Emit the file-loaded event with the file
        emit('file-loaded', file);
      } else {
        createToast({
          type: 'error',
          message: 'Invalid file format. Please upload a valid MusicXML file (.xml, .musicxml, or .mxl).'
        });
      }
    }
  } catch (error) {
    console.error('Error picking file:', error);
    // Only show error toast if it's not a user cancellation
    if ((error as Error).message !== 'User cancelled the file picker') {
      createToast({
        type: 'error',
        message: 'Error selecting file. Please try again.'
      });
    }
  }
};

// Handle drag events
const handleDragEnter = () => {
  dragCounter.value++;
  dragActive.value = true;
};

const handleDragLeave = () => {
  dragCounter.value--;
  if (dragCounter.value === 0) {
    dragActive.value = false;
  }
};

const handleDrop = async (e: DragEvent) => {
  dragActive.value = false;
  if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
    const file = e.dataTransfer.files[0];
    if (isValidFile(file.name)) {
      // Emit the file-loaded event with the file
      emit('file-loaded', file);
    } else {
      createToast({
        type: 'error',
        message: 'Invalid file format. Please upload a valid MusicXML file (.xml, .musicxml, or .mxl).'
      });
    }
  }
};
</script>

<template>
  <div 
    class="w-full grow rounded-xl border-2 border-dashed border-text-tertiary transition-colors cursor-pointer"
    :class="{ 
      'bg-surface/30 border-primary': dragActive, 
      'bg-surface': !dragActive,
      'p-8': !dense,
      'p-1': dense
    }"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent
    @drop.prevent="handleDrop"
    @click="triggerFileInput"
  >
    <div 
      :class="{ 
        'flex-col items-center justify-center space-y-4': !dense, 
        'flex-row items-center justify-center space-x-4': dense 
      }" 
      class="flex h-full"
    >
      <div :class="{ 'text-4xl': !dense, 'text-2xl': dense }" class="text-text-tertiary">
        <svg xmlns="http://www.w3.org/2000/svg" :class="{ 'w-12 h-12': !dense, 'w-12 h-12 opacity-50': dense }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      </div>
      <div class="text-center">
        <p
          v-if="!isMobileDevice"
          :class="{ 'text-lg': !dense, 'text-base': dense }"
          class="font-medium text-text-primary"
        >
          Drag & Drop your MusicXML file here
        </p>
        <p class="text-sm text-text-secondary">Click to browse your files</p>
        <p class="text-xs text-text-primary mt-1">Supported formats: .xml, .musicxml, .mxl</p>
      </div>
    </div>
    
    <!-- <div v-if="sheetMusic.error.value" class="mt-4 text-center p-4 bg-danger/20 rounded-xl">
      <p class="text-text-primary">{{ sheetMusic.error }}</p>
    </div> -->
  </div>
</template> 